import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight, ArrowRightLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useMealTranslation } from '../translations/meals';
import { useTranslation } from '../translations';
import { useLanguageStore } from '../store/languageStore';
import { FoodSubstitutionModal } from './FoodSubstitutionModal';
import type { Meal } from '../types';

interface DietPlanMealProps {
  meal: Meal;
  index: number;
  allMeals: Meal[];
  onAddFood: () => void;
  onDeleteFood: (mealFoodId: string) => void;
  onUpdatePortion: (mealFoodId: string, newGrams: number) => void;
  onSubstituteFood: () => void;
  onTransferFood: () => void;
  deleteLoading: boolean;
  mealMacros: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
}

export default function DietPlanMeal({
  meal,
  index,
  allMeals,
  onAddFood,
  onDeleteFood,
  onUpdatePortion,
  onSubstituteFood,
  onTransferFood,
  deleteLoading,
  mealMacros
}: DietPlanMealProps) {
  const { translateMeal } = useMealTranslation();
  const { t } = useTranslation();
  const language = useLanguageStore(state => state.language);

  const getFoodName = (food: { name: string; name_en?: string | null }) => {
    if (language === 'en' && food.name_en) {
      return food.name_en;
    }
    return food.name;
  };

  const [editingPortionId, setEditingPortionId] = useState<string | null>(null);
  const [tempPortionValue, setTempPortionValue] = useState<string>('');
  const [substitutionModalOpen, setSubstitutionModalOpen] = useState(false);
  const [selectedFoodForSubstitution, setSelectedFoodForSubstitution] = useState<any>(null);
  const [transferDropdownOpen, setTransferDropdownOpen] = useState<string | null>(null);
  const [transferring, setTransferring] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const availableMeals = allMeals.filter(m => m.id !== meal.id);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setTransferDropdownOpen(null);
      }
    }

    if (transferDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [transferDropdownOpen]);

  const handleTransferFood = async (mealFoodId: string, targetMealId: string) => {
    setTransferring(true);
    try {
      const { error } = await supabase
        .from('meal_foods')
        .update({ meal_id: targetMealId })
        .eq('id', mealFoodId);

      if (error) throw error;

      setTransferDropdownOpen(null);
      onTransferFood();
    } catch (error) {
      console.error('Error transferring food:', error);
      alert(language === 'pt'
        ? 'Erro ao transferir alimento. Tente novamente.'
        : 'Error transferring food. Please try again.');
    } finally {
      setTransferring(false);
    }
  };

  const handlePortionChange = (mealFoodId: string, currentGrams: number, increment: boolean) => {
    const step = 10; // Adjust portion by 10g increments
    const minPortion = 25; // Minimum 25g portion
    const maxPortion = 300; // Maximum 300g portion

    const newGrams = increment
      ? Math.min(maxPortion, currentGrams + step)
      : Math.max(minPortion, currentGrams - step);

    onUpdatePortion(mealFoodId, newGrams);
  };

  const handlePortionClick = (mealFoodId: string, currentGrams: number) => {
    setEditingPortionId(mealFoodId);
    setTempPortionValue(currentGrams.toString());
  };

  const handlePortionInputChange = (value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      setTempPortionValue(value);
    }
  };

  const handlePortionInputBlur = (mealFoodId: string) => {
    const newGrams = parseInt(tempPortionValue) || 25;
    const minPortion = 25;
    const maxPortion = 300;
    const validGrams = Math.min(maxPortion, Math.max(minPortion, newGrams));

    onUpdatePortion(mealFoodId, validGrams);
    setEditingPortionId(null);
  };

  const handlePortionInputKeyDown = (e: React.KeyboardEvent, mealFoodId: string) => {
    if (e.key === 'Enter') {
      handlePortionInputBlur(mealFoodId);
    } else if (e.key === 'Escape') {
      setEditingPortionId(null);
    }
  };

  return (
    <div className="bg-[rgb(28,28,28)] rounded-lg shadow-lg border border-[#f8c045]/10">
      {/* Header */}
      <div className="p-4 border-b border-[#f8c045]/10">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-[#f8c045]">
              {translateMeal(meal.name) || `${t('meal')} ${index + 1}`}
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              {mealMacros.calories} {t('calories')}
            </p>
          </div>
          <button
            onClick={onAddFood}
            className="flex items-center text-[#f8c045] hover:text-[#e6b041] transition"
          >
            <Plus size={20} className="mr-1" />
            {t('addFood')}
          </button>
        </div>
      </div>

      {/* Food List */}
      <div className="divide-y divide-[#f8c045]/10">
        {meal.meal_foods?.map((mealFood) => {
          const portion = Math.round(mealFood.quantity * mealFood.food.portion_size);
          const calories = Math.round(mealFood.food.calories * mealFood.quantity);
          const protein = (mealFood.food.protein * mealFood.quantity).toFixed(1);
          const carbs = (mealFood.food.carbs * mealFood.quantity).toFixed(1);
          const fats = (mealFood.food.fats * mealFood.quantity).toFixed(1);

          return (
            <div key={mealFood.id} className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <h4
                      className="text-white font-medium cursor-pointer hover:text-[#f8c045] transition"
                      onClick={() => {
                        setSelectedFoodForSubstitution({
                          id: mealFood.food.id,
                          name: getFoodName(mealFood.food),
                          protein: mealFood.food.protein,
                          carbs: mealFood.food.carbs,
                          fats: mealFood.food.fats,
                          calories: mealFood.food.calories,
                          quantity: mealFood.quantity
                        });
                        setSubstitutionModalOpen(true);
                      }}
                      title={language === 'pt' ? 'Clique para substituir' : 'Click to substitute'}
                    >
                      {getFoodName(mealFood.food)}
                    </h4>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handlePortionChange(mealFood.id, portion, false)}
                        className="text-[#f8c045] hover:text-[#e6b041] transition p-1"
                        title={t('decreasePortion')}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      {editingPortionId === mealFood.id ? (
                        <input
                          type="text"
                          value={tempPortionValue}
                          onChange={(e) => handlePortionInputChange(e.target.value)}
                          onBlur={() => handlePortionInputBlur(mealFood.id)}
                          onKeyDown={(e) => handlePortionInputKeyDown(e, mealFood.id)}
                          className="w-16 bg-[rgb(23,23,23)] text-[#f8c045] text-center rounded border border-[#f8c045] focus:outline-none focus:ring-2 focus:ring-[#f8c045]/50 px-1 py-0.5"
                          autoFocus
                        />
                      ) : (
                        <span
                          onClick={() => handlePortionClick(mealFood.id, portion)}
                          className="text-[#f8c045] min-w-[3ch] text-center cursor-pointer hover:bg-[rgb(23,23,23)] px-2 py-0.5 rounded transition"
                        >
                          {portion}g
                        </span>
                      )}
                      <button
                        onClick={() => handlePortionChange(mealFood.id, portion, true)}
                        className="text-[#f8c045] hover:text-[#e6b041] transition p-1"
                        title={t('increasePortion')}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-400">
                    <span>{calories} kcal</span>
                    <span className="mx-2">•</span>
                    <span>P: {protein}g</span>
                    <span className="mx-2">•</span>
                    <span>C: {carbs}g</span>
                    <span className="mx-2">•</span>
                    <span>G: {fats}g</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <div className="relative" ref={transferDropdownOpen === mealFood.id ? dropdownRef : null}>
                    <button
                      onClick={() => setTransferDropdownOpen(transferDropdownOpen === mealFood.id ? null : mealFood.id)}
                      className="text-gray-400 hover:text-[#f8c045] transition"
                      title={language === 'pt' ? 'Transferir para outra refeição' : 'Transfer to another meal'}
                      disabled={transferring}
                    >
                      <ArrowRightLeft size={18} />
                    </button>

                    {transferDropdownOpen === mealFood.id && availableMeals.length > 0 && (
                      <div className="absolute right-0 mt-2 w-56 bg-[rgb(23,23,23)] border border-[#f8c045]/30 rounded-lg shadow-xl z-50">
                        <div className="p-2 border-b border-[#f8c045]/20">
                          <p className="text-xs text-gray-400">
                            {language === 'pt' ? 'Transferir para:' : 'Transfer to:'}
                          </p>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {availableMeals.map((targetMeal) => (
                            <button
                              key={targetMeal.id}
                              onClick={() => handleTransferFood(mealFood.id, targetMeal.id)}
                              disabled={transferring}
                              className="w-full text-left px-4 py-3 hover:bg-[rgb(28,28,28)] transition text-white border-b border-[#f8c045]/10 last:border-b-0 disabled:opacity-50"
                            >
                              <div className="font-medium">{translateMeal(targetMeal.name)}</div>
                              <div className="text-xs text-gray-400 mt-0.5">
                                {targetMeal.meal_foods?.length || 0} {language === 'pt' ? 'alimentos' : 'foods'}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => onDeleteFood(mealFood.id)}
                    className="text-gray-400 hover:text-red-500 transition"
                    disabled={deleteLoading}
                  >
                    <Trash2 size={18} className={deleteLoading ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer with Totals */}
      <div className="p-4 border-t border-[#f8c045]/10 bg-[rgb(23,23,23)]">
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-400">{t('total')}</span>
          <div className="flex items-center space-x-4 text-[#f8c045]">
            <span>P: {Math.round(mealMacros.protein)}g</span>
            <span>C: {Math.round(mealMacros.carbs)}g</span>
            <span>G: {Math.round(mealMacros.fats)}g</span>
            <span className="font-semibold">{mealMacros.calories} kcal</span>
          </div>
        </div>
      </div>

      {selectedFoodForSubstitution && (
        <FoodSubstitutionModal
          isOpen={substitutionModalOpen}
          onClose={() => {
            setSubstitutionModalOpen(false);
            setSelectedFoodForSubstitution(null);
          }}
          currentFood={selectedFoodForSubstitution}
          mealId={meal.id}
          onSubstitute={() => {
            onSubstituteFood();
            setSubstitutionModalOpen(false);
            setSelectedFoodForSubstitution(null);
          }}
        />
      )}
    </div>
  );
}